import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save } from "lucide-react";
import { db } from "../../firebase/config";
import {
  collection,
  doc,
  setDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged, IdTokenResult } from "firebase/auth";
import ContentLoader from "@/authentication/ContentLoader";
import { useToast } from "@/components/ui/use-toast";

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface DormInput {
  id?: string;
  name: string;
  location: string;
  capacity: number;
  occupied: number;
  available: number;
  patronTeacherId?: string | null;
  dormUid?: string;
  submittedBy: string;
  schoolID: string;
  createdAt: Date;
}

interface TeacherData {
  id: string;
  firstName: string;
  lastName: string;
  employeeId: string;
}

interface DormRegistrationFormProps {
  onBack: () => void;
  onSave: (updatedDorm: DormInput) => void;
}

const DormRegistrationForm: React.FC<DormRegistrationFormProps> = ({
  onBack,
  onSave,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Auth / school state
  const [userId, setUserId] = useState<string | null>(null);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);

  // Form state
  const [formData, setFormData] = useState<DormInput>({
    name: "",
    location: "",
    capacity: 0,
    occupied: 0,
    available: 0,
    patronTeacherId: null,
    submittedBy: "",
    schoolID: "",
    createdAt: new Date(),
  });

  // Teachers
  const [teachers, setTeachers] = useState<TeacherData[]>([]);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(true);

  /* ── Auth & Claims Verification ──────────────────────────────────── */
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        try {
          if (process.env.NODE_ENV !== "production") {
            console.log("Authenticating user with uid:", user.uid);
          }
          // Refresh token to ensure claims are up-to-date
          await user.getIdToken(true);
          const idTokenResult: IdTokenResult = await user.getIdTokenResult(true);
          if (!idTokenResult) {
            throw new Error("Failed to retrieve user token claims");
          }
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          if (process.env.NODE_ENV !== "production") {
            console.log("ID Token Result claims:", JSON.stringify(idTokenResult.claims, null, 2));
            console.log("Affiliations:", affiliations, "Default Affiliation:", defaultAffiliation, "Claim schoolID:", claimSchoolID);
          }

          // Select admin affiliation
          let selectedAffiliation: Affiliation | null = null;
          if (defaultAffiliation && defaultAffiliation.role === "admin" && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => aff.role === "admin" && aff.schoolID) || affiliations[0];
          }

          if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
            const selectedSchoolID = selectedAffiliation.schoolID;
            if (process.env.NODE_ENV !== "production") {
              console.log("Using schoolID from selectedAffiliation:", selectedSchoolID);
            }
            setSchoolID(selectedSchoolID);
            setHasValidClaims(true);
          } else {
            // Retry once after delay to handle claim propagation
            if (process.env.NODE_ENV !== "production") {
              console.log("Invalid or missing admin claims, retrying after 10 seconds...");
            }
            await new Promise(resolve => setTimeout(resolve, 10000));
            await user.getIdToken(true);
            const retryIdTokenResult = await user.getIdTokenResult(true);
            const retryAffiliations = (retryIdTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
            const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as Affiliation | undefined;
            const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

            if (process.env.NODE_ENV !== "production") {
              console.log("Retry claims:", JSON.stringify(retryIdTokenResult.claims, null, 2));
            }

            let retrySelectedAffiliation: Affiliation | null = null;
            if (retryDefaultAff && retryDefaultAff.role === "admin" && retryDefaultAff.schoolID) {
              retrySelectedAffiliation = retryDefaultAff;
            } else if (retryAffiliations.length > 0) {
              retrySelectedAffiliation = retryAffiliations.find(aff => aff.role === "admin" && aff.schoolID) || retryAffiliations[0];
            }

            if (retrySelectedAffiliation?.role === "admin" && retrySelectedAffiliation.schoolID && retrySchoolID === retrySelectedAffiliation.schoolID) {
              setSchoolID(retrySelectedAffiliation.schoolID);
              setHasValidClaims(true);
            } else {
              throw new Error(
                `Invalid token claims: affiliations=${JSON.stringify(retryAffiliations)}, ` +
                `defaultAffiliation=${JSON.stringify(retryDefaultAff)}, schoolID=${retrySchoolID}`
              );
            }
          }
        } catch (error: any) {
          console.error("Error verifying user claims:", error);
          toast({
            title: "Missing Permissions",
            description: "You lack the necessary permissions to register a dormitory. Please contact support.",
            variant: "destructive",
            duration: 5000,
          });
          setHasValidClaims(false);
          navigate("/login-page");
        }
      } else {
        setUserId(null);
        setSchoolID(null);
        setHasValidClaims(false);
        toast({
          title: "Authentication Error",
          description: "No user logged in. Please log in and try again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsAuthLoading(false);
    });

    return () => {
      if (process.env.NODE_ENV !== "production") {
        console.log("Unsubscribing from onAuthStateChanged");
      }
      unsubscribe();
    };
  }, [navigate, toast]);

  /* ── Fetch teachers ───────────────────────────────────── */
  const fetchTeachers = useCallback(async () => {
    if (!hasValidClaims || !schoolID) {
      setIsLoadingTeachers(false);
      return;
    }

    setIsLoadingTeachers(true);
    try {
      const teachersRef = collection(db, `schools/${schoolID}/teachers`);
      const teacherSnapshot = await getDocs(teachersRef);
      const teacherList: TeacherData[] = teacherSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as TeacherData[];
      setTeachers(teacherList);
    } catch (error: any) {
      console.error("Error fetching teachers from Firestore:", error);
      toast({
        title: "Error Loading Teachers",
        description: `Could not load teacher data: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsLoadingTeachers(false);
    }
  }, [schoolID, hasValidClaims, toast]);

  useEffect(() => {
    if (!isAuthLoading && hasValidClaims && schoolID) {
      fetchTeachers();
    } else {
      setIsLoadingTeachers(false);
    }
  }, [isAuthLoading, hasValidClaims, schoolID, fetchTeachers]);

  /* ── Input change ──────────────────────────────────────── */
  const handleInputChange = (field: keyof DormInput, value: string | null) => {
    setFormData((prev) => {
      if (field === "capacity") {
        if (value === null) {
          return {
            ...prev,
            [field]: 0,
            available: 0,
          };
        }
        const parsedValue = parseInt(value) || 0;
        return {
          ...prev,
          [field]: parsedValue,
          available: parsedValue,
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  /* ── Submit ────────────────────────────────────────────── */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!schoolID || !userId || !hasValidClaims) {
      toast({
        title: "Authentication Error",
        description: "User, school ID, or permissions missing. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/login-page");
      return;
    }

    const user = getAuth().currentUser;
    if (!user) {
      toast({
        title: "Authentication Error",
        description: "User is not authenticated. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/login-page");
      return;
    }

    const dormNameTrimmed = formData.name.trim();
    const locationTrimmed = formData.location.trim();
    const capacity = formData.capacity;

    if (capacity <= 0) {
      toast({
        title: "Invalid Capacity",
        description: "Dormitory capacity must be a positive number.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    if (!dormNameTrimmed || !locationTrimmed) {
      toast({
        title: "Invalid Input",
        description: "Dormitory name and location cannot be empty.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      // Re-verify claims before Firestore write
      await user.getIdToken(true);
      const idTokenResult = await user.getIdTokenResult(true);
      const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
      const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
      const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

      if (process.env.NODE_ENV !== "production") {
        console.log("Claims before write:", JSON.stringify({ affiliations, defaultAffiliation, claimSchoolID }, null, 2));
      }

      // Verify admin role and schoolID
      const adminAffiliation = affiliations.find(aff => aff.role === "admin" && aff.schoolID === schoolID);
      if (
        !adminAffiliation &&
        (!defaultAffiliation || defaultAffiliation.role !== "admin" || defaultAffiliation.schoolID !== schoolID) &&
        claimSchoolID !== schoolID
      ) {
        // Retry once after delay
        if (process.env.NODE_ENV !== "production") {
          console.log("Invalid admin claims, retrying after 10 seconds...");
        }
        await new Promise(resolve => setTimeout(resolve, 10000));
        await user.getIdToken(true);
        const retryIdTokenResult = await user.getIdTokenResult(true);
        const retryAffiliations = (retryIdTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const retryClaimSchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

        if (process.env.NODE_ENV !== "production") {
          console.log("Retry claims:", JSON.stringify({ retryAffiliations, retryDefaultAff, retryClaimSchoolID }, null, 2));
        }

        const retryAdminAffiliation = retryAffiliations.find(aff => aff.role === "admin" && aff.schoolID === schoolID);
        if (
          !retryAdminAffiliation &&
          (!retryDefaultAff || retryDefaultAff.role !== "admin" || retryDefaultAff.schoolID !== schoolID) &&
          retryClaimSchoolID !== schoolID
        ) {
          throw new Error(
            `Invalid token claims: affiliations=${JSON.stringify(retryAffiliations)}, ` +
            `defaultAffiliation=${JSON.stringify(retryDefaultAff)}, schoolID=${retryClaimSchoolID}`
          );
        }
      }

      // Check for duplicate dorm name
      const dormsCollectionRef = collection(db, `schools/${schoolID}/dorms`);
      const q = query(dormsCollectionRef, where("name", "==", dormNameTrimmed));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        toast({
          title: "Dorm Name Already Exists",
          description: `A dormitory named "${dormNameTrimmed}" already exists in your school. Please choose a different name.`,
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      // Write to Firestore
      const dormDocRef = doc(dormsCollectionRef);
      const dormUid = dormDocRef.id;

      const dormData: DormInput = {
        name: dormNameTrimmed,
        location: locationTrimmed,
        capacity,
        occupied: 0,
        available: capacity,
        patronTeacherId: formData.patronTeacherId,
        submittedBy: userId,
        schoolID: schoolID,
        dormUid,
        createdAt: new Date(),
      };

      try {
        await setDoc(dormDocRef, dormData);
        toast({
          title: "Dorm Registered Successfully!",
          description: `${dormNameTrimmed} has been registered successfully to Firebase.`,
          duration: 3000,
        });

        onSave(dormData);

        setFormData({
          name: "",
          location: "",
          capacity: 0,
          occupied: 0,
          available: 0,
          patronTeacherId: null,
          submittedBy: "",
          schoolID: "",
          createdAt: new Date(),
        });

        setTimeout(() => {
          navigate("/dorms");
        }, 1500);
      } catch (error: any) {
        if (error.code === "permission-denied") {
          console.error("Permission denied during Firestore write:", error);
          toast({
            title: "Permission Error",
            description: "Unable to register dormitory due to insufficient permissions. Please ensure your account is set up as an admin and try again.",
            variant: "destructive",
            duration: 5000,
          });
        } else {
          throw error;
        }
      }
    } catch (error: any) {
      console.error("Error registering dorm to Firebase:", error);
      toast({
        title: "Registration Failed",
        description: `Failed to register dormitory: ${error.message || "An unexpected error occurred. Please try again."}`,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  /* ── Back ──────────────────────────────────────────────── */
  const handleBack = () => {
    if (process.env.NODE_ENV !== "production") {
      console.log("Back to Dormitories clicked");
    }
    onBack();
    navigate("/dorms");
  };

  /* ── Loading UI handled by ContentLoader ─────────────────────────────── */

  /* ── Unauthorized UI ───────────────────────────────────── */
  if (!isAuthLoading && !hasValidClaims) {
    return (
      <div className="flex h-full w-full justify-center items-center p-6 bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md border-none bg-white dark:bg-gray-800">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl text-gray-900 dark:text-white">
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-400">
              You do not have the necessary permissions to register a dormitory. Please contact support or re-login.
            </p>
            <Button
              onClick={() => navigate("/login-page")}
              className="mt-4 bg-blue-600 text-white hover:bg-blue-700"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ── Main UI ───────────────────────────────────────────── */
  return (
    <ContentLoader isLoading={isAuthLoading || (hasValidClaims && isLoadingTeachers)} message="Loading dormitory registration form...">
      <div className="w-full h-full bg-gray-50 dark:bg-gray-900">
        <div className="mb-4 sm:mb-6">
        <Button
          onClick={handleBack}
          variant="outline"
          className="mb-3 sm:mb-4 flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dormitories
        </Button>

        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Register New Dormitory
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Add a new dormitory to the school system
        </p>
      </div>

      <Card className="w-full max-w-2xl mx-auto border-none bg-white dark:bg-gray-800">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl text-gray-900 dark:text-white">
            Dormitory Information
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-900 dark:text-white"
                >
                  Dormitory Name
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange("name", e.target.value)
                  }
                  placeholder="Enter dormitory name"
                  className="w-full bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="location"
                  className="text-sm font-medium text-gray-900 dark:text-white"
                >
                  Location
                </Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange("location", e.target.value)
                  }
                  placeholder="Enter dormitory location"
                  className="w-full bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="capacity"
                  className="text-sm font-medium text-gray-900 dark:text-white"
                >
                  Capacity
                </Label>
                <Input
                  id="capacity"
                  type="number"
                  min="1"
                  value={formData.capacity.toString()}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange("capacity", e.target.value)
                  }
                  placeholder="Enter dormitory capacity"
                  className="w-full bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="patron"
                  className="text-sm font-medium text-gray-900 dark:text-white"
                >
                  Dorm Patron (Optional)
                </Label>
                <Select
                  value={formData.patronTeacherId || "none"}
                  onValueChange={(value: string) =>
                    handleInputChange("patronTeacherId", value === "none" ? null : value)
                  }
                >
                  <SelectTrigger className="w-full bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    <SelectValue placeholder="Select a teacher as dorm patron" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                    <SelectItem
                      value="none"
                      className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      No Patron Assigned
                    </SelectItem>
                    {teachers.map((teacher) => (
                      <SelectItem
                        key={teacher.id}
                        value={teacher.id}
                        className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        {teacher.firstName} {teacher.lastName} ({teacher.employeeId})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="w-full sm:w-auto order-2 sm:order-1 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="w-full sm:w-auto flex items-center justify-center gap-2 order-1 sm:order-2 bg-blue-600 text-white hover:bg-blue-700"
              >
                <Save className="h-4 w-4" />
                Register Dormitory
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  </ContentLoader>
  );
};

export default DormRegistrationForm;